/**
 * Monatsraster eines Apartments: Wochentage, sechs Wochen mit Tageszellen und die Aufenthalte als
 * Balken je Woche – Quelle, Nächte, geplante Abreise; vergangene Aufenthalte gedämpft. Der Balken
 * informiert, er verlinkt nicht: das Lesemodell trägt keine Buchungsidentität.
 */
import { cn } from '@/shared/lib/cn';
import {
  rangeIndexAt,
  type SelectedRange,
  type SelectionMode,
} from '../calendar/rangeSelectionModel';
import { useAutomationLayer } from '../automation/AutomationLayerContext';
import { ownedCell } from '../automation/automationModel';
import { MonthBar } from './MonthBar';
import { MonthCell } from './MonthCell';
import type { MonthWeek } from './monthModel';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

interface Props {
  propertyId: string;
  /** Handy: 64-px-Zellen, Aufenthalte als Streifen. */
  compact?: boolean;
  weeks: MonthWeek[];
  currency: string;
  today: string;
  ranges: readonly SelectedRange[];
  onBegin?: (date: string, mode: SelectionMode, immediate?: boolean) => void;
  onExtend?: (date: string) => void;
}

export function MonthGrid({
  propertyId,
  compact = false,
  weeks,
  currency,
  today,
  ranges,
  onBegin,
  onExtend,
}: Props) {
  const layer = useAutomationLayer();
  return (
    <div>
      <div className="border-hairline grid grid-cols-7 border-b" aria-hidden>
        {WEEKDAYS.map((weekday) => (
          <span
            key={weekday}
            className={cn(
              'text-ink-soft flex items-center justify-center font-mono text-[10px] font-semibold uppercase tracking-[0.12em]',
              compact ? 'h-[34px]' : 'h-10',
            )}
          >
            {weekday}
          </span>
        ))}
      </div>
      {weeks.map((week, row) => (
        <div
          key={week.cells[0]?.day.date ?? row}
          className="border-card-alt relative grid grid-cols-7 border-b"
        >
          {week.cells.map((cell, column) => {
            const index = rangeIndexAt(ranges, cell.day.date);
            const range = ranges[index];
            return (
              <MonthCell
                key={cell.day.date}
                cell={cell}
                currency={currency}
                today={today}
                first={row === 0 && column === 0}
                selected={index >= 0}
                automation={ownedCell(layer?.cell(propertyId, cell.day.date) ?? null, cell.day)}
                edge={
                  range !== undefined &&
                  (range.from === cell.day.date || range.to === cell.day.date)
                }
                compact={compact}
                onBegin={onBegin}
                onExtend={onExtend}
              />
            );
          })}
          {week.bars.map((placed) => (
            <MonthBar key={placed.bar.key} placed={placed} today={today} compact={compact} />
          ))}
        </div>
      ))}
    </div>
  );
}
